import { createContext, useCallback, useContext, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { Lion } from '@/components/brand/Lion'
import { Button } from '@/components/ui/Button'
import { ErrorState, Loading } from '@/components/ui/States'
import { supabase, describeError } from '@/lib/supabase'
import { SignInPage } from './SignInPage'
import { MfaChallenge } from './MfaChallenge'
import { SetPasswordPrompt, needsPassword } from './SetPasswordPrompt'
import { useAssuranceLevel, useSession, useStaff, type StaffRecord } from './useSession'

/**
 * The gate on the whole internal app.
 *
 * Everything below this component can assume a signed-in, active staff member
 * whose session already satisfies their MFA requirement — which is the same set
 * of conditions `is_staff()` checks in the database. That duplication is
 * deliberate: the database is the boundary that matters, and this is only here
 * so a user who fails it sees an explanation instead of a screen of empty tables.
 */

interface StaffContextValue {
  staff: StaffRecord
  isOwner: boolean
  signOut: () => Promise<void>
}

const StaffContext = createContext<StaffContextValue | null>(null)

export function useCurrentStaff(): StaffContextValue {
  const value = useContext(StaffContext)
  if (!value) throw new Error('useCurrentStaff was called outside RequireStaff.')
  return value
}

export function RequireStaff({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const { session, loading: sessionLoading } = useSession()
  const staffQuery = useStaff(session)
  const aalQuery = useAssuranceLevel(session)

  // Someone who arrived by invitation or by an emailed link has no password yet.
  // Dismissing is remembered for this session only, so it asks again next time
  // rather than nagging on every navigation.
  const [passwordPromptDismissed, setPasswordPromptDismissed] = useState(false)

  const signOut = useCallback(async () => {
    await supabase.auth.signOut()
    queryClient.clear()
  }, [queryClient])

  if (sessionLoading) return <Loading label="Opening the studio" />
  if (!session) return <SignInPage />

  const aal = aalQuery.data
  const needsSecondFactor = aal?.nextLevel === 'aal2' && aal.currentLevel === 'aal1'

  // Asked before the staff lookup: with mfa_required set, the staff row is not
  // readable at aal1, so waiting for that query would deadlock on a challenge
  // the user has not been shown yet.
  if (needsSecondFactor) return <MfaChallenge onSignOut={() => void signOut()} />

  if (staffQuery.isPending || aalQuery.isPending) return <Loading label="Opening the studio" />

  if (staffQuery.isError) {
    return (
      <Centred>
        <ErrorState
          title="Could not load your account"
          detail={describeError(staffQuery.error)}
          onRetry={() => void staffQuery.refetch()}
        />
        <SignOutLink onSignOut={signOut} />
      </Centred>
    )
  }

  const staff = staffQuery.data

  if (!staff) {
    return (
      <Centred>
        <div className="text-center">
          <Lion size={44} className="mx-auto" />
          <h1 className="mt-5 font-[family-name:var(--font-heading)] text-[0.875rem] font-medium">
            This account has no studio access
          </h1>
          <p className="measure mx-auto mt-3 text-[length:var(--text-sm)] text-taupe">
            You are signed in as {session.user.email}, but that address is not on the staff list. If it should be, ask
            Jena to add it under Settings, then sign in again.
          </p>
        </div>
        <SignOutLink onSignOut={signOut} />
      </Centred>
    )
  }

  if (staff.deactivated_at) {
    return (
      <Centred>
        <div className="text-center">
          <Lion size={44} className="mx-auto" />
          <h1 className="mt-5 font-[family-name:var(--font-heading)] text-[0.875rem] font-medium">
            This account has been deactivated
          </h1>
          <p className="measure mx-auto mt-3 text-[length:var(--text-sm)] text-taupe">
            Access to {session.user.email} was turned off on {staff.deactivated_at.slice(0, 10)}.
          </p>
        </div>
        <SignOutLink onSignOut={signOut} />
      </Centred>
    )
  }

  // Asked after the staff check, not before: someone who is not staff should be
  // told that, not invited to choose a password for an account with no access.
  if (needsPassword(session.user) && !passwordPromptDismissed) {
    return (
      <SetPasswordPrompt
        email={session.user.email ?? staff.email}
        onDone={() => {
          setPasswordPromptDismissed(true)
          void queryClient.invalidateQueries({ queryKey: ['staff'] })
        }}
      />
    )
  }

  return (
    <StaffContext.Provider value={{ staff, isOwner: staff.role === 'owner', signOut }}>
      {children}
    </StaffContext.Provider>
  )
}

function Centred({ children }: { children: ReactNode }) {
  return (
    <main className="grid min-h-dvh place-items-center px-5 py-12">
      <div className="flex w-full max-w-[26rem] flex-col items-center gap-6">{children}</div>
    </main>
  )
}

function SignOutLink({ onSignOut }: { onSignOut: () => Promise<void> }) {
  return (
    <Button variant="secondary" size="sm" onClick={() => void onSignOut()}>
      Sign out
    </Button>
  )
}
